import React, { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search,
  Sparkles,
  UtensilsCrossed,
  ArrowRight,
  Filter,
  CheckCircle2,
} from "lucide-react";
import { staticMenuItems, menuCategories } from "../data/menu";
import { businessInfo } from "../data/business";

export default function PublicMenu() {
  const [searchParams] = useSearchParams();
  const tableParam = searchParams.get("table");

  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredItems = useMemo(() => {
    return staticMenuItems.filter((item) => {
      const matchCategory =
        activeCategory === "all" || item.category === activeCategory;
      const matchSearch =
        !searchQuery ||
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchCategory && matchSearch;
    });
  }, [activeCategory, searchQuery]);

  return (
    <div className="space-y-10 sm:space-y-14 pb-16">
      {/* 🍨 HEADER */}
      <section className="bg-gradient-to-b from-sky-50/70 to-transparent pt-8 sm:pt-14 pb-6 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-sky-200 text-sky-800 text-xs sm:text-sm font-bold shadow-xs">
            <Sparkles className="w-4 h-4 text-sky-600" />
            <span>100% Real Fruit • Pure Fresh Milk</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#2C3E50] tracking-tight">
            Our Menu Showcase
          </h1>

          <p className="text-sm sm:text-base text-stone-600 max-w-2xl mx-auto">
            Explore our handcrafted natural ice cream flavours, rich milkshakes, royal faludas, and authentic slow-cooked rabdi.
          </p>

          {/* DINE IN BANNER */}
          <div className="mt-6 max-w-2xl mx-auto p-4 rounded-2xl bg-gradient-to-r from-amber-500/10 via-sky-500/10 to-teal-500/10 border border-sky-200/80 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-xs">
            <div className="flex items-center gap-3 text-left">
              <div className="w-10 h-10 rounded-xl bg-sky-500 text-white flex items-center justify-center shrink-0">
                <UtensilsCrossed className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-bold text-[#2C3E50]">
                  {tableParam
                    ? `Ordering for Table ${tableParam}?`
                    : "Dining at our Udaipur café?"}
                </p>
                <p className="text-xs text-stone-500">
                  Select your table to browse the interactive ordering menu & place orders.
                </p>
              </div>
            </div>

            <Link
              to={tableParam ? `/order?table=${tableParam}` : "/select-table"}
              className="bg-sky-500 hover:bg-sky-600 text-white text-xs sm:text-sm font-bold px-4 py-2.5 rounded-xl shadow whitespace-nowrap transition"
            >
              {tableParam ? "Go to Order Menu" : "Select Your Table"}
            </Link>
          </div>
        </div>
      </section>

      {/* 🔍 SEARCH & CATEGORY FILTERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          
          {/* CATEGORY TABS */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
            {menuCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-bold transition-all whitespace-nowrap ${
                  activeCategory === cat.id
                    ? "bg-[#2C3E50] text-white shadow-md"
                    : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-50 hover:text-[#2C3E50]"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* SEARCH BOX */}
          <div className="relative min-w-[260px] max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search flavours, shakes, faluda..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-xs sm:text-sm bg-white border border-stone-200 rounded-full focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition shadow-xs"
            />
          </div>

        </div>
      </section>

      {/* 🍨 PRODUCT SHOWCASE GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {filteredItems.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-stone-200 p-8 space-y-3">
            <p className="text-3xl">🍨</p>
            <h3 className="font-bold text-lg text-[#2C3E50]">No items found</h3>
            <p className="text-xs sm:text-sm text-stone-500">
              Try searching with another keyword or pick a different category.
            </p>
            <button
              onClick={() => {
                setActiveCategory("all");
                setSearchQuery("");
              }}
              className="mt-2 text-xs font-bold text-sky-600 underline"
            >
              Reset filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-3xl overflow-hidden border border-stone-200/80 shadow-xs hover:shadow-lg transition-all duration-300 group flex flex-col justify-between"
              >
                <div>
                  {/* Image container */}
                  <div className="relative h-48 overflow-hidden bg-stone-100">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {item.badge && (
                      <span className="absolute top-3 right-3 bg-stone-900/80 backdrop-blur-md text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow">
                        {item.badge}
                      </span>
                    )}
                    <span className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-md text-[#2C3E50] text-[11px] font-semibold px-2 py-0.5 rounded-lg shadow-xs">
                      {item.categoryName}
                    </span>
                  </div>

                  {/* Body details */}
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h3 className="font-bold text-base sm:text-lg text-[#2C3E50]">
                        {item.name}
                      </h3>
                      <span className="font-extrabold text-base sm:text-lg text-sky-600 shrink-0">
                        ₹{item.price}
                      </span>
                    </div>

                    <p className="text-xs text-stone-600 leading-relaxed line-clamp-2">
                      {item.description}
                    </p>
                  </div>
                </div>

                {/* Footer action */}
                <div className="px-5 pb-5 pt-3 border-t border-stone-100 flex items-center justify-between text-xs">
                  <span className="text-[11px] text-stone-400">
                    100% Real Dairy
                  </span>
                  <Link
                    to="/select-table"
                    className="font-bold text-sky-600 hover:text-sky-700 inline-flex items-center gap-1 hover:underline"
                  >
                    <span>Order at Table</span>
                    <span>→</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* FOOTER CTA BANNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-6 sm:p-8 rounded-3xl bg-stone-900 text-white flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl">
          <div>
            <h3 className="text-lg sm:text-xl font-bold">
              Looking for custom party packs or bulk tubs?
            </h3>
            <p className="text-xs sm:text-sm text-stone-400 mt-0.5">
              Contact our Udaipur shop directly for catering, family packs, and event scoops.
            </p>
          </div>

          <a
            href={`tel:${businessInfo.contact.phone}`}
            className="bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs sm:text-sm px-6 py-3 rounded-full shadow transition whitespace-nowrap"
          >
            Call {businessInfo.contact.phoneDisplay}
          </a>
        </div>
      </section>
    </div>
  );
}
